import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { LoadingState } from '@/components/StateMessage/StateMessage';
import styles from '@/pages/ProjectsPage.module.css';

type RefreshLogRow = {
  id: string;
  definition_version: number;
  lead_count: number;
  account_count: number;
  removed_count: number;
  created_at: string;
};

export function AudienceDynamicRefreshPanel({
  audienceId,
  audienceType,
  hasSegment,
  refreshEnabled,
  refreshRemoveNonMatching,
  lastRefreshedAt,
}: {
  audienceId: string;
  audienceType: string;
  hasSegment: boolean;
  refreshEnabled: boolean;
  refreshRemoveNonMatching: boolean;
  lastRefreshedAt: string | null;
}) {
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState(refreshEnabled);
  const [removePolicy, setRemovePolicy] = useState(refreshRemoveNonMatching);

  useEffect(() => {
    setEnabled(refreshEnabled);
    setRemovePolicy(refreshRemoveNonMatching);
  }, [refreshEnabled, refreshRemoveNonMatching]);

  const logQ = useQuery({
    queryKey: ['audience-refresh-log', audienceId],
    queryFn: () =>
      apiFetch<{ log: RefreshLogRow[] }>(`/api/audiences/${audienceId}/refresh-log?limit=5`),
  });

  const saveSettings = useMutation({
    mutationFn: () =>
      apiFetch(`/api/audiences/${audienceId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          audience_type: hasSegment ? 'dynamic' : undefined,
          refresh_enabled: enabled,
          refresh_remove_non_matching: removePolicy,
        }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['audience', audienceId] });
    },
  });

  const refreshNow = useMutation({
    mutationFn: () =>
      apiFetch(`/api/audiences/${audienceId}/refresh`, { method: 'POST', body: '{}' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['audience', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audience-refresh-log', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audience-members', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audiences'] });
    },
  });

  if (!hasSegment) {
    return (
      <p className={styles.mutedSmall}>
        Sla eerst een filterdefinitie op om dynamische refresh te gebruiken.
      </p>
    );
  }

  return (
    <section className={styles.card} style={{ marginTop: 'var(--space-lg)' }}>
      <h3>Dynamische refresh</h3>
      <p className={styles.mutedSmall}>
        Worker job <code>audience:refresh</code> draait dagelijks (~03:00 UTC) voor doelgroepen met
        automatische refresh. Type: {audienceType === 'dynamic' ? 'Dynamisch' : 'Statisch'}.
      </p>

      <label className={styles.row}>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />{' '}
        Automatische refresh inschakelen
      </label>
      <label className={styles.row}>
        <input
          type="checkbox"
          checked={removePolicy}
          onChange={(e) => setRemovePolicy(e.target.checked)}
        />{' '}
        Verwijder contacten die niet meer aan filters voldoen (alle bronnen)
      </label>

      <div className={styles.row} style={{ marginTop: 'var(--space-md)' }}>
        <Button
          type="button"
          variant="secondary"
          onClick={() => saveSettings.mutate()}
          disabled={saveSettings.isPending}
        >
          Instellingen opslaan
        </Button>
        <Button type="button" onClick={() => refreshNow.mutate()} disabled={refreshNow.isPending}>
          Nu verversen
        </Button>
      </div>

      <p className={styles.mutedSmall}>
        Laatste run:{' '}
        {lastRefreshedAt ? new Date(lastRefreshedAt).toLocaleString('nl-NL') : '—'}
      </p>

      {logQ.isLoading ? <LoadingState /> : null}
      {(logQ.data?.log.length ?? 0) > 0 ? (
        <>
          <h4>Recente runs</h4>
          <ul className={styles.list}>
            {logQ.data!.log.map((row) => (
              <li key={row.id} className={styles.mutedSmall}>
                {new Date(row.created_at).toLocaleString('nl-NL')} · v{row.definition_version} ·{' '}
                {row.lead_count} contacten · {row.removed_count} verwijderd
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}
